import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { CircleNotch, FloppyDisk, X } from "@phosphor-icons/react";
import { ClientPriceCalculator } from "@/components/pricing/ClientPriceCalculator";
import { CountryMultiplierPreview } from "@/components/pricing/CountryMultiplierPreview";
import { ServicePriceLimitHint } from "@/components/pricing/ServicePriceLimitHint";
import { useServicePriceError } from "@/hooks/useServicePriceLimits";
import { usePricingConfig } from "@/hooks/usePricingConfig";
import {
  EMPTY_SERVICE_BASELINE,
  serviceSaveGrossPrice,
  type ServiceSavedBaseline,
} from "@/lib/service-price-save";
import { useTranslation } from "react-i18next";
import { SERVICE_DURATION_MAX, SERVICE_DURATION_MIN } from "@/schemas/service";
import { ServiceMembersField } from "@/components/session-members/ServiceMembersField";

export interface ServiceFormData {
  name: string;
  description: string;
  /** null = prazno polje; provjera pri spremanju javlja grešku umjesto da pošalje 0. */
  durationMinutes: number | null;
  price: number | null;
  modality: "online" | "in_person" | "both";
  maxMembers: number | null;
}

export const emptyForm: ServiceFormData = {
  name: "",
  description: "",
  durationMinutes: 50,
  price: null,
  modality: "online",
  maxMembers: null,
};

interface ServiceFormProps {
  form: ServiceFormData;
  setForm: React.Dispatch<React.SetStateAction<ServiceFormData>>;
  editingId: string | null;
  isSaving: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  /** Baseline (cijena bez PDV-a + sirovi bruto) cijene usluge koja se uredjuje; nepromijenjena
   *  cijena je uvijek dopustena, cak i van trenutnog raspona. */
  savedBaseline?: ServiceSavedBaseline;
}

export function ServiceForm({ form, setForm, editingId, isSaving, onSubmit, onCancel, savedBaseline }: ServiceFormProps) {
  const { t } = useTranslation();
  const pricingConfig = usePricingConfig();
  // The form captures the service price without VAT, and the admin range is set for that
  // same number, so the hint checks the field value directly. An untouched price (equal to
  // the loaded baseline) is grandfathered, the same rule resolveServiceSavePrice applies.
  const baseline = savedBaseline ?? EMPTY_SERVICE_BASELINE;
  const priceError = useServicePriceError(form.price, baseline.base);
  // Gross the form will store (untouched price keeps its raw stored gross).
  const submittedPrice =
    form.price === null || !pricingConfig.config
      ? null
      : serviceSaveGrossPrice(form.price, baseline, pricingConfig.config);
  return (
    <Card className="border-[#ece8df]">
      <CardHeader>
        <CardTitle style={{ fontFamily: "var(--font-heading)" }}>
          {editingId ? "Uredi uslugu" : "Nova usluga"}
        </CardTitle>
        <CardDescription>
          {editingId
            ? "Izmijenite podatke o usluzi"
            : "Popunite podatke za novu uslugu"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="service-name">Naziv usluge</Label>
          <Input
            id="service-name"
            value={form.name}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, name: e.target.value }))
            }
            placeholder="npr. Individualna terapija"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-desc">Opis</Label>
          <Textarea
            id="service-desc"
            value={form.description}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            placeholder="Opišite uslugu koju nudite..."
            className="min-h-24"
          />
          <p className="text-xs text-muted-foreground">
            Ako opis ostavite prazan, klijenti će umjesto njega vidjeti naziv usluge.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="service-duration">Trajanje (minuta)</Label>
            <Input
              id="service-duration"
              type="number"
              min={SERVICE_DURATION_MIN}
              max={SERVICE_DURATION_MAX}
              value={form.durationMinutes ?? ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  durationMinutes: e.target.value === "" ? null : Number(e.target.value),
                }))
              }
            />
            <p className="text-xs text-muted-foreground">
              Od {SERVICE_DURATION_MIN} do {SERVICE_DURATION_MAX} minuta.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="service-price">Cijena usluge (bez PDV-a)</Label>
            <CurrencyInput
              id="service-price"
              value={form.price}
              onChange={(v) => setForm((prev) => ({ ...prev, price: v }))}
              aria-invalid={Boolean(priceError)}
            />
            <ServicePriceLimitHint price={form.price} savedPrice={baseline.base} />
          </div>
        </div>

        <ClientPriceCalculator amount={form.price} pricing={pricingConfig} baseline={baseline} />

        <div className="space-y-2">
          <Label htmlFor="service-modality">{t("services.modalityLabel")}</Label>
          <Select
            value={form.modality}
            onValueChange={(v) =>
              setForm((prev) => ({ ...prev, modality: v as ServiceFormData["modality"] }))
            }
          >
            <SelectTrigger id="service-modality" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="online">{t("services.modalityOnline")}</SelectItem>
              <SelectItem value="in_person">{t("services.modalityInPerson")}</SelectItem>
              <SelectItem value="both">{t("services.modalityBoth")}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{t("services.modalityHelp")}</p>
        </div>

        <ServiceMembersField
          value={form.maxMembers}
          onChange={(maxMembers) => setForm((prev) => ({ ...prev, maxMembers }))}
        />

        {/* Server mnozi spremljeni BRUTO koeficijentom zemlje, pa pregled mora krenuti od
            iznosa koji ce se stvarno spremiti, ne od upisane cijene bez PDV-a. */}
        <CountryMultiplierPreview clientPrice={submittedPrice ?? 0} />
      </CardContent>
      <CardFooter className="flex-col items-start gap-2">
        <div className="flex gap-3">
          <Button
            onClick={onSubmit}
            disabled={isSaving || Boolean(priceError) || !pricingConfig.config}
          >
            {isSaving ? (
              <CircleNotch className="size-4 animate-spin" />
            ) : (
              <FloppyDisk className="size-4" />
            )}
            {editingId ? "Spremi izmjene" : "Kreiraj uslugu"}
          </Button>
          <Button variant="outline" onClick={onCancel}>
            <X className="size-4" />
            Otkaži
          </Button>
        </div>
        {!pricingConfig.config && (
          <p className="text-xs text-muted-foreground">
            {pricingConfig.isError
              ? "Trenutno nije moguće učitati konfiguraciju cijena. Osvježite stranicu i pokušajte ponovo."
              : "Cijene se učitavaju."}
          </p>
        )}
      </CardFooter>
    </Card>
  );
}
